import React, { useState, useEffect } from 'react';

const UserMatching = ({ userProfile, onSelectMatch, onBack }) => {
  const [matches, setMatches] = useState([]);
  const [filters, setFilters] = useState({
    ageRange: { min: 18, max: 50 },
    interests: [],
    socialGoals: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  // Sample users for matching
  const sampleUsers = [
    {
      id: "user_001",
      name: "Alex Chen", 
      bigFive: {openness: 85, conscientiousness: 70, extraversion: 75, agreeableness: 80, neuroticism: 25},
      demographics: {age: 28, location: {city: "San Francisco", state: "CA"}, education: "masters"},
      interests: ["technology", "hiking", "photography", "cooking"],
      socialGoals: "networking",
      communicationStyle: "intellectual"
    },
    {
      id: "user_002", 
      name: "Morgan Rodriguez",
      bigFive: {openness: 90, conscientiousness: 60, extraversion: 85, agreeableness: 85, neuroticism: 30},
      demographics: {age: 26, location: {city: "Austin", state: "TX"}, education: "bachelors"},
      interests: ["music", "art", "travel", "dancing"], 
      socialGoals: "friendship",
      communicationStyle: "storytelling"
    },
    {
      id: "user_003",
      name: "Jordan Williams",
      bigFive: {openness: 65, conscientiousness: 85, extraversion: 60, agreeableness: 90, neuroticism: 20},
      demographics: {age: 32, location: {city: "Denver", state: "CO"}, education: "masters"},
      interests: ["fitness", "reading", "volunteering", "cooking"],
      socialGoals: "friendship", 
      communicationStyle: "direct"
    },
    {
      id: "user_004",
      name: "Casey Thompson", 
      bigFive: {openness: 95, conscientiousness: 55, extraversion: 90, agreeableness: 75, neuroticism: 35},
      demographics: {age: 24, location: {city: "Miami", state: "FL"}, education: "bachelors"},
      interests: ["parties", "beach", "socializing", "nightlife"],
      socialGoals: "dating",
      communicationStyle: "humor_based"
    },
    {
      id: "user_005",
      name: "Riley Park",
      bigFive: {openness: 80, conscientiousness: 90, extraversion: 45, agreeableness: 85, neuroticism: 15},
      demographics: {age: 29, location: {city: "Seattle", state: "WA"}, education: "doctorate"},
      interests: ["research", "books", "coffee", "museums"], 
      socialGoals: "intellectual_connection",
      communicationStyle: "intellectual"
    }
  ];

  useEffect(() => {
    // Simulate loading and calculate matches
    setTimeout(() => {
      const calculatedMatches = calculateMatches();
      setMatches(calculatedMatches);
      setIsLoading(false);
    }, 1500);
  }, [userProfile]);

  const calculateMatches = () => {
    if (!userProfile?.personality?.bigFive) {
      return [];
    }

    const userBigFive = userProfile.personality.bigFive;
    
    return sampleUsers.map(candidate => {
      // Calculate personality compatibility
      const personalityScore = calculatePersonalityCompatibility(userBigFive, candidate.bigFive);
      
      // Calculate interest overlap
      const interestScore = calculateInterestOverlap(
        userProfile.interests || [], 
        candidate.interests
      );
      
      // Calculate communication style compatibility
      const communicationScore = calculateCommunicationCompatibility(
        userProfile.personality.communicationStyle,
        candidate.communicationStyle
      );
      
      // Calculate overall compatibility
      const overall = (personalityScore * 0.5 + interestScore * 0.3 + communicationScore * 0.2);
      
      // Generate match reasons
      const matchReasons = generateMatchReasons(
        userBigFive,
        candidate.bigFive,
        userProfile.interests || [],
        candidate.interests
      );
      
      return {
        ...candidate,
        compatibility: {
          overall,
          breakdown: {
            personality: personalityScore,
            interests: interestScore,
            communication: communicationScore
          }
        },
        matchReasons
      };
    }).sort((a, b) => b.compatibility.overall - a.compatibility.overall);
  };

  const calculatePersonalityCompatibility = (user, candidate) => {
    const traits = ['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism'];
    let totalDifference = 0;
    
    traits.forEach(trait => {
      const userScore = user[trait] || 50;
      const candidateScore = candidate[trait] || 50;
      
      // For neuroticism, lower is better for both
      if (trait === 'neuroticism') {
        totalDifference += Math.abs(userScore - candidateScore) / 100;
      } else {
        // For other traits, some difference is good, but not too much
        const diff = Math.abs(userScore - candidateScore);
        const optimalDiff = 20; // Some difference is healthy
        
        if (diff <= optimalDiff) {
          totalDifference += (optimalDiff - diff) / 100;
        } else {
          totalDifference += Math.max(0, 100 - diff) / 100;
        }
      }
    });
    
    return Math.max(0, 1 - totalDifference / traits.length);
  };

  const calculateInterestOverlap = (userInterests, candidateInterests) => {
    if (!userInterests.length || !candidateInterests.length) {
      return 0.5; // Neutral score if no interests provided
    }
    
    const overlap = userInterests.filter(interest => 
      candidateInterests.includes(interest)
    ).length;
    
    const totalUnique = new Set([...userInterests, ...candidateInterests]).size;
    
    return overlap / Math.max(userInterests.length, candidateInterests.length);
  };

  const calculateCommunicationCompatibility = (userStyle, candidateStyle) => {
    const compatibilityMatrix = {
      'intellectual': { 'intellectual': 0.9, 'direct': 0.7, 'storytelling': 0.6, 'humor_based': 0.5, 'casual': 0.6 },
      'direct': { 'intellectual': 0.7, 'direct': 0.9, 'storytelling': 0.5, 'humor_based': 0.6, 'casual': 0.7 },
      'storytelling': { 'intellectual': 0.6, 'direct': 0.5, 'storytelling': 0.9, 'humor_based': 0.8, 'casual': 0.7 },
      'humor_based': { 'intellectual': 0.5, 'direct': 0.6, 'storytelling': 0.8, 'humor_based': 0.9, 'casual': 0.8 },
      'casual': { 'intellectual': 0.6, 'direct': 0.7, 'storytelling': 0.7, 'humor_based': 0.8, 'casual': 0.9 }
    };
    
    return compatibilityMatrix[userStyle]?.[candidateStyle] || 0.5;
  };

  const generateMatchReasons = (userBigFive, candidateBigFive, userInterests, candidateInterests) => {
    const reasons = [];
    
    // Personality-based reasons
    if (Math.abs(userBigFive.extraversion - candidateBigFive.extraversion) < 20) {
      reasons.push("Similar social energy levels");
    }
    
    if (userBigFive.openness > 70 && candidateBigFive.openness > 70) {
      reasons.push("Both value creativity and new experiences");
    }
    
    if (userBigFive.agreeableness > 70 && candidateBigFive.agreeableness > 70) {
      reasons.push("Both prioritize harmony and cooperation");
    }
    
    // Interest-based reasons
    const sharedInterests = userInterests.filter(i => candidateInterests.includes(i));
    if (sharedInterests.length > 0) {
      reasons.push(`Shared interests in ${sharedInterests.slice(0, 2).join(' and ')}`);
    }
    
    return reasons.slice(0, 3);
  };

  const MatchCard = ({ match }) => {
    const compatPercent = Math.round(match.compatibility.overall * 100);
    
    return (
      <div className="bg-gray-800 rounded-lg p-6 mb-4">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-white">{match.name}</h3>
            <p className="text-gray-400">
              {match.demographics.age} • {match.demographics.location.city}, {match.demographics.location.state}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-500">{compatPercent}%</div>
            <div className="text-xs text-gray-400">Match</div>
          </div>
        </div>
        
        <div className="mb-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {match.interests.map(interest => (
              <span key={interest} className="px-2 py-1 bg-gray-700 rounded-full text-xs text-gray-300">
                {interest}
              </span>
            ))}
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Personality</span>
              <span className="text-gray-300">
                {Math.round(match.compatibility.breakdown.personality * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-1.5">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-1.5 rounded-full"
                style={{ width: `${match.compatibility.breakdown.personality * 100}%` }}
              />
            </div>
          </div>
        </div>
        
        {match.matchReasons.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">Why you match:</p>
            <ul className="text-sm text-gray-300 space-y-1">
              {match.matchReasons.map((reason, idx) => (
                <li key={idx}>• {reason}</li>
              ))}
            </ul>
          </div>
        )}
        
        <button
          onClick={() => onSelectMatch(match)}
          className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          View Conversation Starters
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="text-gray-400 mt-4">Finding your perfect matches...</p>
      </div>
    );
  }

  return (
    <div className="user-matching">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white mb-4"
        >
          ← Back to Results
        </button>
        
        <h2 className="text-2xl font-bold text-white mb-2">
          Your Matches
        </h2>
        <p className="text-gray-400">
          {matches.length} compatible people found based on your personality
        </p>
      </div>
      
      <div className="matches-list">
        {matches.map(match => (
          <MatchCard key={match.id} match={match} />
        ))}
      </div>
    </div>
  );
};

export default UserMatching;